import { forwardRef } from "react";
import { useTranslation } from "next-i18next";
import { BaseInputProps } from "@talladega/types";
import { customerValidation } from "@talladega/helpers";
import { usePhoneNumberMask } from "@talladega/hooks";
import { BaseInput } from "../form";
import { getInputStyles, getBaseInputProps } from "../helpers";

// eslint-disable-next-line no-underscore-dangle
const _Phone = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      type = "tel",
      id = "phoneNumber",
      disabled,
      autoComplete = "tel",
      defaultValue,
      className,
      suppressHotjar = true,
      fieldError,
      onChange,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation(["common-components"]);
    const { maskPipe: maskPhoneNumberPipe, unMaskPipe: unMaskPhoneNumberPipe } =
      usePhoneNumberMask();
    const getFeedback = () => {
      if (!fieldError?.message) {
        if (fieldError?.type === "required") {
          return t("common-components:required");
        }
        if (fieldError?.type === "valid") {
          return t("common-components:phone_min_length");
        }
        if (fieldError?.type === "validAreaCode") {
          return t("common-components:form_error_invalid_phone_number");
        }
      }
      return fieldError?.message;
    };
    const baseInputProps = getBaseInputProps({
      type,
      disabled,
      label: props.label || t("common-components:phone_number") || undefined,
      status: fieldError ? "error" : "default",
      feedback: getFeedback(),
      defaultValue: maskPhoneNumberPipe(defaultValue ?? ""),
      ...props,
    });
    const inputClassName = getInputStyles({
      leftIcon: baseInputProps.leftIcon,
      rightIcon: baseInputProps.rightIcon,
      border: baseInputProps.border,
      placeholderSize: baseInputProps.placeholderSize,
      status: baseInputProps.status,
    });
    return (
      <BaseInput id={id} className={className} {...baseInputProps}>
        <input
          id={id}
          className={inputClassName}
          type={type}
          autoComplete={autoComplete}
          maxLength={customerValidation.phoneNumber.maxLength}
          defaultValue={baseInputProps.defaultValue}
          label={baseInputProps.label}
          disabled={disabled}
          ref={ref}
          data-hj-allow={(!suppressHotjar && type === "text") || undefined}
          onChange={(e) => {
            const unmasked = unMaskPhoneNumberPipe(e.target.value);
            const masked = maskPhoneNumberPipe(unmasked);
            e.target.value = masked;

            if (onChange) onChange(e);
          }}
          {...props}
        />
      </BaseInput>
    );
  }
);

export default _Phone;
